import { useCallback, useState } from 'react';

/**
 * localStorage-backed replacement for `useState`, so filter selections
 * survive a reload/navigation instead of resetting to the first option every
 * time. Deliberately narrow: callers pass an `isValid` guard so a value from
 * an old app version, a manually edited option list, or a corrupted/foreign
 * value in storage falls back to `initialValue` rather than crashing the page
 * or feeding a stale shape into state.
 *
 * Not for anything identifying a specific request or patient - see call
 * sites for which fields are deliberately left out.
 */
export const usePersistedState = <T>(
  key: string,
  initialValue: T,
  isValid: (value: unknown) => value is T,
) => {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return initialValue;
      const parsed = JSON.parse(raw);
      return isValid(parsed) ? parsed : initialValue;
    } catch {
      return initialValue;
    }
  });

  // Stable across renders (only `key` can change it) so it is safe to leave
  // out of a `useCallback`/`useEffect` dependency array, the same guarantee
  // `useState`'s own setter gives.
  const setPersisted = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Storage can throw under quota limits or private-browsing
        // restrictions; state still updates for the current page, it just
        // won't survive the next reload.
      }
    },
    [key],
  );

  return [value, setPersisted] as const;
};

/** Type guard factory: value is an `Option`-shaped object present in `options`. */
export const isOptionIn =
  <T extends { value: string }>(options: T[]) =>
  (value: unknown): value is T =>
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    options.some(
      (option) => option.value === (value as { value: unknown }).value,
    );

export const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean';

export const isString = (value: unknown): value is string =>
  typeof value === 'string';

export const isNumberIn =
  (values: number[]) =>
  (value: unknown): value is number =>
    typeof value === 'number' && values.includes(value);
